import { useState, useEffect } from 'react';

/**
 * Hook to track page visibility and prevent unnecessary data fetching
 * when user switches tabs and comes back
 */
export const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [lastVisibleTime, setLastVisibleTime] = useState(Date.now());

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsVisible(visible);
      
      if (visible) {
        setLastVisibleTime(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    isVisible,
    lastVisibleTime,
    wasHiddenFor: (milliseconds: number) => {
      return Date.now() - lastVisibleTime > milliseconds;
    }
  };
};

/**
 * Hook to determine if data should be refreshed based on visibility and cache age
 */
export const useShouldRefresh = (
  lastFetchTime: number,
  maxAge: number = 5 * 60 * 1000 // 5 minutes default
) => {
  const { isVisible, wasHiddenFor } = usePageVisibility();
  
  const shouldRefresh = 
    isVisible && 
    (Date.now() - lastFetchTime > maxAge || wasHiddenFor(maxAge));
    
  return shouldRefresh;
};
